import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
    Zap,
    MessageCircle,
    UserPlus,
    BarChart2,
    RefreshCw,
    CheckCircle,
    Trash2,
    Plus,
    X,
    Play,
    Pause,
    Info,
    Users,
    TrendingUp,
} from 'lucide-react';
import {
    GlassCard,
    GlassButton,
    GlassInput,
    GlassSelect,
    GlassTabs,
    GlassBadge,
    ModuleHeader,
} from '../ui';
import {
    getOrganicGrowthHealth,
    getEngagementQueue,
    populateEngagementQueue,
    approveEngagementDraft,
    discardEngagementDraft,
    getDMFlows,
    createDMFlow,
    updateDMFlow,
    deleteDMFlow,
    getFollowQueue,
    populateFollowQueue,
    executeFollow,
    skipFollow,
    getOrganicGrowthAnalytics,
    getSocialAccounts,
} from '../../services/api';
import './OrganicGrowth.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface HealthStats {
    connected_accounts: number;
    pending_drafts: number;
    active_dm_flows: number;
    follows_this_week: number;
    accounts_in_ramp: number;
}

interface SocialAccountItem {
    account_id: string;
    platform: string;
    username?: string;
    display_name?: string;
    status: string;
    ramp_ends_at?: string;
}

interface EngagementDraft {
    draft_id: string;
    account_id: string;
    platform: string;
    target_post_snippet?: string;
    draft_text: string;
    draft_type: string;
    status: string;
    created_at?: string;
}

interface DMFlowItem {
    flow_id: string;
    account_id: string;
    platform: string;
    name: string;
    trigger_type: string;
    trigger_value?: string;
    steps: { delay_minutes: number; message: string }[];
    status: string;
    sent_count: number;
    reply_count: number;
    created_at?: string;
}

interface FollowItem {
    follow_id: string;
    account_id: string;
    platform: string;
    target_handle: string;
    target_profile_url?: string;
    reason?: string;
    status: string;
    created_at?: string;
}

// ---------------------------------------------------------------------------
// Platform follow limits for display
// ---------------------------------------------------------------------------
const PLATFORM_DAILY_LIMITS: Record<string, number> = {
    instagram: 200,
    twitter: 400,
    linkedin: 15,
    facebook: 20,
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const OrganicGrowthModule: React.FC = () => {
    const ctx = useOutletContext<{ selectedProjectId?: string }>();
    const selectedProjectId = ctx?.selectedProjectId;

    const [activeTab, setActiveTab] = useState('overview');
    const [health, setHealth] = useState<HealthStats | null>(null);
    const [accounts, setAccounts] = useState<SocialAccountItem[]>([]);
    const [drafts, setDrafts] = useState<EngagementDraft[]>([]);
    const [draftEdits, setDraftEdits] = useState<Record<string, string>>({});
    const [draftPlatformFilter, setDraftPlatformFilter] = useState('all');
    const [flows, setFlows] = useState<DMFlowItem[]>([]);
    const [followItems, setFollowItems] = useState<FollowItem[]>([]);
    const [analytics, setAnalytics] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);

    // Overview actions
    const [selectedAccountId, setSelectedAccountId] = useState('');
    const [populatingDrafts, setPopulatingDrafts] = useState(false);
    const [populatingFollows, setPopulatingFollows] = useState(false);

    // New DM flow form
    const [showNewFlowForm, setShowNewFlowForm] = useState(false);
    const [newFlow, setNewFlow] = useState({
        account_id: '',
        name: '',
        trigger_type: 'keyword_comment',
        trigger_value: '',
        steps: [{ delay_minutes: 0, message: '' }],
    });

    const tabs = [
        { id: 'overview', label: 'Overview' },
        { id: 'queue', label: 'Engagement Queue' },
        { id: 'dmflows', label: 'DM Flows' },
        { id: 'follows', label: 'Follow Manager' },
        { id: 'analytics', label: 'Analytics' },
    ];

    const showSuccess = (msg: string) => {
        setSuccessMsg(msg);
        setTimeout(() => setSuccessMsg(null), 3000);
    };

    // ---------------------------------------------------------------------------
    // Data loaders
    // ---------------------------------------------------------------------------
    const loadHealth = useCallback(async () => {
        try {
            const data = await getOrganicGrowthHealth(selectedProjectId);
            setHealth(data);
        } catch (e: any) {
            console.error('Health load failed', e);
        }
    }, [selectedProjectId]);

    const loadAccounts = useCallback(async () => {
        try {
            const data = await getSocialAccounts();
            setAccounts(data.filter((a: SocialAccountItem) => a.status === 'active'));
        } catch (e: any) {
            console.error('Accounts load failed', e);
        }
    }, []);

    const loadDrafts = useCallback(async () => {
        try {
            const platform = draftPlatformFilter !== 'all' ? draftPlatformFilter : undefined;
            const data = await getEngagementQueue(platform, selectedProjectId);
            setDrafts(data);
        } catch (e: any) {
            console.error('Drafts load failed', e);
        }
    }, [draftPlatformFilter, selectedProjectId]);

    const loadFlows = useCallback(async () => {
        try {
            const data = await getDMFlows(selectedProjectId);
            setFlows(data);
        } catch (e: any) {
            console.error('DM flows load failed', e);
        }
    }, [selectedProjectId]);

    const loadFollows = useCallback(async () => {
        try {
            const data = await getFollowQueue(selectedProjectId);
            setFollowItems(data);
        } catch (e: any) {
            console.error('Follow queue load failed', e);
        }
    }, [selectedProjectId]);

    const loadAnalytics = useCallback(async () => {
        try {
            const data = await getOrganicGrowthAnalytics(selectedProjectId);
            setAnalytics(data);
        } catch (e: any) {
            console.error('Analytics load failed', e);
        }
    }, [selectedProjectId]);

    useEffect(() => {
        if (!selectedProjectId) return;
        loadHealth();
        loadAccounts();
    }, [loadHealth, loadAccounts, selectedProjectId]);

    useEffect(() => {
        if (!selectedProjectId) return;
        if (activeTab === 'queue') loadDrafts();
        if (activeTab === 'dmflows') loadFlows();
        if (activeTab === 'follows') loadFollows();
        if (activeTab === 'analytics') loadAnalytics();
    }, [activeTab, loadDrafts, loadFlows, loadFollows, loadAnalytics, selectedProjectId]);

    // Reload drafts when filter changes
    useEffect(() => {
        if (!selectedProjectId) return;
        if (activeTab === 'queue') loadDrafts();
    }, [draftPlatformFilter, activeTab, loadDrafts, selectedProjectId]);

    // ---------------------------------------------------------------------------
    // Actions
    // ---------------------------------------------------------------------------
    const handlePopulateDrafts = async () => {
        if (!selectedAccountId) {
            setError('Select a social account first');
            return;
        }
        setPopulatingDrafts(true);
        setError(null);
        try {
            const result = await populateEngagementQueue(selectedAccountId);
            if (result.skipped) {
                setError(result.reason || 'Account is in ramp period');
            } else {
                showSuccess(`Generated ${result.drafts_created} engagement drafts`);
                loadHealth();
                loadDrafts();
            }
        } catch (e: any) {
            setError(getApiError(e, 'Failed to generate drafts'));
        } finally {
            setPopulatingDrafts(false);
        }
    };

    const handlePopulateFollows = async () => {
        if (!selectedAccountId) {
            setError('Select a social account first');
            return;
        }
        setPopulatingFollows(true);
        setError(null);
        try {
            const result = await populateFollowQueue(selectedAccountId);
            if (result.skipped) {
                setError(result.reason || 'Rate limit reached');
            } else {
                showSuccess(`Generated ${result.suggestions_created} follow suggestions`);
                loadFollows();
            }
        } catch (e: any) {
            setError(getApiError(e, 'Failed to generate follow suggestions'));
        } finally {
            setPopulatingFollows(false);
        }
    };

    const handleApproveDraft = async (draftId: string) => {
        try {
            await approveEngagementDraft(draftId);
            setDrafts(prev => prev.filter(d => d.draft_id !== draftId));
            loadHealth();
            showSuccess('Draft approved');
        } catch (e: any) {
            setError(getApiError(e, 'Failed to approve draft'));
        }
    };

    const handleDiscardDraft = async (draftId: string) => {
        try {
            await discardEngagementDraft(draftId);
            setDrafts(prev => prev.filter(d => d.draft_id !== draftId));
            loadHealth();
        } catch (e: any) {
            setError(getApiError(e, 'Failed to discard draft'));
        }
    };

    const handleApproveAll = async () => {
        for (const d of drafts) {
            try {
                await approveEngagementDraft(d.draft_id);
            } catch (e) {
                console.error('Approve failed for', d.draft_id);
            }
        }
        setDrafts([]);
        loadHealth();
        showSuccess('All drafts approved');
    };

    const handleToggleFlowStatus = async (flow: DMFlowItem) => {
        const newStatus = flow.status === 'active' ? 'paused' : 'active';
        try {
            await updateDMFlow(flow.flow_id, { status: newStatus });
            setFlows(prev => prev.map(f => f.flow_id === flow.flow_id ? { ...f, status: newStatus } : f));
        } catch (e: any) {
            setError(getApiError(e, 'Failed to update flow'));
        }
    };

    const handleDeleteFlow = async (flowId: string) => {
        try {
            await deleteDMFlow(flowId);
            setFlows(prev => prev.filter(f => f.flow_id !== flowId));
        } catch (e: any) {
            setError(getApiError(e, 'Failed to delete flow'));
        }
    };

    const handleCreateFlow = async () => {
        if (!newFlow.account_id || !newFlow.name) {
            setError('Account and flow name are required');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const created = await createDMFlow({
                account_id: newFlow.account_id,
                name: newFlow.name,
                trigger_type: newFlow.trigger_type,
                trigger_value: newFlow.trigger_value,
                steps: newFlow.steps.filter(s => s.message.trim()),
            });
            setFlows(prev => [created, ...prev]);
            setShowNewFlowForm(false);
            setNewFlow({ account_id: '', name: '', trigger_type: 'keyword_comment', trigger_value: '', steps: [{ delay_minutes: 0, message: '' }] });
            showSuccess('DM flow created');
        } catch (e: any) {
            setError(getApiError(e, 'Failed to create flow'));
        } finally {
            setLoading(false);
        }
    };

    const handleExecuteFollow = async (followId: string) => {
        try {
            await executeFollow(followId);
            setFollowItems(prev => prev.filter(f => f.follow_id !== followId));
            showSuccess('Follow executed');
        } catch (e: any) {
            setError(getApiError(e, 'Rate limit reached or follow failed'));
        }
    };

    const handleSkipFollow = async (followId: string) => {
        try {
            await skipFollow(followId);
            setFollowItems(prev => prev.filter(f => f.follow_id !== followId));
        } catch (e: any) {
            setError(getApiError(e, 'Failed to skip'));
        }
    };

    const handleFollowAll = async () => {
        let count = 0;
        for (const item of followItems) {
            try {
                await executeFollow(item.follow_id);
                count++;
            } catch {
                break; // Stop at rate limit
            }
        }
        loadFollows();
        showSuccess(`Followed ${count} accounts`);
    };

    // ---------------------------------------------------------------------------
    // Account options for selects
    // ---------------------------------------------------------------------------
    const accountOptions = accounts.map(a => ({
        value: a.account_id,
        label: `${a.platform} — ${a.username || a.display_name || a.account_id.slice(0, 8)}`,
    }));

    const triggerTypeOptions = [
        { value: 'keyword_comment', label: 'Keyword in Comment' },
        { value: 'bio_link_click', label: 'Bio Link Click' },
        { value: 'story_reply', label: 'Story Reply' },
    ];

    const platformOptions = ['all', 'instagram', 'facebook', 'linkedin', 'twitter'];

    // ---------------------------------------------------------------------------
    // Render helpers
    // ---------------------------------------------------------------------------
    const renderOverview = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Stats row */}
            <div className="og-stats-row">
                <div className="og-stat-box">
                    <span className="og-stat-label">Connected Accounts</span>
                    <span className="og-stat-value">{health?.connected_accounts ?? '—'}</span>
                </div>
                <div className="og-stat-box">
                    <span className="og-stat-label">Pending Drafts</span>
                    <span className="og-stat-value">{health?.pending_drafts ?? '—'}</span>
                </div>
                <div className="og-stat-box">
                    <span className="og-stat-label">Active DM Flows</span>
                    <span className="og-stat-value">{health?.active_dm_flows ?? '—'}</span>
                </div>
                <div className="og-stat-box">
                    <span className="og-stat-label">Follows This Week</span>
                    <span className="og-stat-value">{health?.follows_this_week ?? '—'}</span>
                </div>
            </div>

            {/* Ramp alert */}
            {(health?.accounts_in_ramp ?? 0) > 0 && (
                <div className="og-ramp-alert">
                    <Info size={16} />
                    <span>
                        <strong>{health!.accounts_in_ramp}</strong> account{health!.accounts_in_ramp > 1 ? 's are' : ' is'} in the
                        ramp period. Engagement actions will begin in week 5 after connection.
                    </span>
                </div>
            )}

            {/* Per-platform accounts */}
            {accounts.length > 0 && (
                <GlassCard>
                    <p className="og-section-heading">Connected Accounts</p>
                    <div className="og-accounts-grid">
                        {accounts.map(acc => {
                            const inRamp = acc.ramp_ends_at && new Date(acc.ramp_ends_at) > new Date();
                            return (
                                <div key={acc.account_id} className="og-account-row">
                                    <span className="og-account-platform">{acc.platform}</span>
                                    <span className="og-account-handle">
                                        {acc.username || acc.display_name || acc.account_id.slice(0, 8)}
                                    </span>
                                    <GlassBadge variant={acc.status === 'active' ? 'success' : 'warning'}>
                                        {acc.status}
                                    </GlassBadge>
                                    {inRamp && <GlassBadge variant="info">Ramp</GlassBadge>}
                                </div>
                            );
                        })}
                    </div>
                </GlassCard>
            )}

            {/* Actions */}
            <GlassCard>
                <p className="og-section-heading">Generate Content</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <GlassSelect
                        options={accountOptions}
                        value={selectedAccountId}
                        onChange={setSelectedAccountId}
                        placeholder="Select social account..."
                        label="Account"
                    />
                    <div className="og-action-panel">
                        <GlassButton
                            variant="primary"
                            onClick={handlePopulateDrafts}
                            disabled={populatingDrafts || !selectedAccountId}
                        >
                            {populatingDrafts ? (
                                <><RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> Generating...</>
                            ) : (
                                <><MessageCircle size={16} /> Generate Engagement Drafts</>
                            )}
                        </GlassButton>
                        <GlassButton
                            variant="secondary"
                            onClick={handlePopulateFollows}
                            disabled={populatingFollows || !selectedAccountId}
                        >
                            {populatingFollows ? (
                                <><RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> Generating...</>
                            ) : (
                                <><UserPlus size={16} /> Get Follow Suggestions</>
                            )}
                        </GlassButton>
                    </div>
                </div>
            </GlassCard>
        </div>
    );

    const renderEngagementQueue = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Platform filter tabs */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                <div className="og-platform-tabs">
                    {platformOptions.map(p => (
                        <button
                            key={p}
                            className={`og-platform-tab ${draftPlatformFilter === p ? 'active' : ''}`}
                            onClick={() => setDraftPlatformFilter(p)}
                        >
                            {p === 'all' ? 'All' : p.charAt(0).toUpperCase() + p.slice(1)}
                        </button>
                    ))}
                </div>
                {drafts.length > 0 && (
                    <GlassButton variant="primary" onClick={handleApproveAll}>
                        <CheckCircle size={16} /> Approve All ({drafts.length})
                    </GlassButton>
                )}
            </div>

            {drafts.length === 0 ? (
                <div className="og-empty">
                    <MessageCircle size={32} style={{ opacity: 0.3, marginBottom: '8px' }} />
                    <p>Queue is empty — generate new drafts from the Overview tab</p>
                </div>
            ) : (
                <div className="og-draft-list">
                    {drafts.map(draft => (
                        <div key={draft.draft_id} className="og-draft-item">
                            <div className="og-draft-meta">
                                <GlassBadge variant="info">{draft.platform}</GlassBadge>
                                <GlassBadge variant="default">{draft.draft_type}</GlassBadge>
                            </div>
                            {draft.target_post_snippet && (
                                <div className="og-draft-snippet">
                                    "{draft.target_post_snippet}"
                                </div>
                            )}
                            <textarea
                                className="og-draft-textarea"
                                value={draftEdits[draft.draft_id] ?? draft.draft_text}
                                onChange={e => setDraftEdits(prev => ({ ...prev, [draft.draft_id]: e.target.value }))}
                            />
                            <div className="og-draft-actions">
                                <GlassButton variant="primary" onClick={() => handleApproveDraft(draft.draft_id)}>
                                    <CheckCircle size={14} /> Approve
                                </GlassButton>
                                <GlassButton variant="secondary" onClick={() => handleDiscardDraft(draft.draft_id)}>
                                    <Trash2 size={14} /> Discard
                                </GlassButton>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    const renderDMFlows = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <GlassButton variant="primary" onClick={() => setShowNewFlowForm(v => !v)}>
                    {showNewFlowForm ? <><X size={16} /> Cancel</> : <><Plus size={16} /> New Flow</>}
                </GlassButton>
            </div>

            {/* New flow form */}
            {showNewFlowForm && (
                <div className="og-new-flow-form">
                    <p className="og-section-heading">Create DM Flow</p>
                    <div className="og-form-row">
                        <div className="og-form-field">
                            <span className="og-form-label">Account</span>
                            <GlassSelect
                                options={accountOptions}
                                value={newFlow.account_id}
                                onChange={v => setNewFlow(prev => ({ ...prev, account_id: v }))}
                                placeholder="Select account..."
                            />
                        </div>
                        <div className="og-form-field">
                            <span className="og-form-label">Flow Name</span>
                            <GlassInput
                                value={newFlow.name}
                                onChange={v => setNewFlow(prev => ({ ...prev, name: v }))}
                                placeholder="e.g. Welcome DM"
                            />
                        </div>
                    </div>
                    <div className="og-form-row">
                        <div className="og-form-field">
                            <span className="og-form-label">Trigger Type</span>
                            <GlassSelect
                                options={triggerTypeOptions}
                                value={newFlow.trigger_type}
                                onChange={v => setNewFlow(prev => ({ ...prev, trigger_type: v }))}
                            />
                        </div>
                        <div className="og-form-field">
                            <span className="og-form-label">Trigger Value (keyword / URL)</span>
                            <GlassInput
                                value={newFlow.trigger_value}
                                onChange={v => setNewFlow(prev => ({ ...prev, trigger_value: v }))}
                                placeholder="e.g. interested"
                            />
                        </div>
                    </div>

                    <div className="og-steps-editor">
                        <span className="og-form-label">Message Steps</span>
                        {newFlow.steps.map((step, idx) => (
                            <div key={idx} className="og-step-row">
                                <div className="og-step-delay">
                                    <GlassInput
                                        value={String(step.delay_minutes)}
                                        onChange={v => {
                                            const steps = [...newFlow.steps];
                                            steps[idx] = { ...steps[idx], delay_minutes: parseInt(v) || 0 };
                                            setNewFlow(prev => ({ ...prev, steps }));
                                        }}
                                        placeholder="0 min"
                                    />
                                </div>
                                <textarea
                                    className="og-step-msg"
                                    value={step.message}
                                    onChange={e => {
                                        const steps = [...newFlow.steps];
                                        steps[idx] = { ...steps[idx], message: e.target.value };
                                        setNewFlow(prev => ({ ...prev, steps }));
                                    }}
                                    placeholder="Message text..."
                                />
                                <GlassButton
                                    variant="secondary"
                                    onClick={() => setNewFlow(prev => ({
                                        ...prev,
                                        steps: prev.steps.filter((_, i) => i !== idx),
                                    }))}
                                >
                                    <X size={14} />
                                </GlassButton>
                            </div>
                        ))}
                        <GlassButton
                            variant="secondary"
                            onClick={() => setNewFlow(prev => ({
                                ...prev,
                                steps: [...prev.steps, { delay_minutes: 60, message: '' }],
                            }))}
                        >
                            <Plus size={14} /> Add Step
                        </GlassButton>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                        <GlassButton variant="primary" onClick={handleCreateFlow} disabled={loading}>
                            {loading ? 'Saving...' : 'Save Flow'}
                        </GlassButton>
                        <GlassButton variant="secondary" onClick={() => setShowNewFlowForm(false)}>
                            Cancel
                        </GlassButton>
                    </div>
                </div>
            )}

            {flows.length === 0 ? (
                <div className="og-empty">
                    <Zap size={32} style={{ opacity: 0.3, marginBottom: '8px' }} />
                    <p>No DM flows yet. Create a flow to automate inbound message responses.</p>
                </div>
            ) : (
                <div className="og-flow-list">
                    {flows.map(flow => (
                        <div key={flow.flow_id} className="og-flow-item">
                            <div className="og-flow-header">
                                <span className="og-flow-name">{flow.name}</span>
                                <GlassBadge variant="info">{flow.platform}</GlassBadge>
                                <GlassBadge
                                    variant={flow.status === 'active' ? 'success' : flow.status === 'paused' ? 'warning' : 'default'}
                                >
                                    {flow.status}
                                </GlassBadge>
                            </div>
                            <div className="og-flow-meta">
                                Trigger: <strong>{flow.trigger_type.replace(/_/g, ' ')}</strong>
                                {flow.trigger_value && <> — "{flow.trigger_value}"</>}
                                {' | '}{flow.steps.length} step{flow.steps.length !== 1 ? 's' : ''}
                            </div>
                            <div className="og-flow-stats">
                                <span>Sent: {flow.sent_count}</span>
                                <span>Replies: {flow.reply_count}</span>
                            </div>
                            <div className="og-flow-actions">
                                <GlassButton
                                    variant="secondary"
                                    onClick={() => handleToggleFlowStatus(flow)}
                                >
                                    {flow.status === 'active' ? <><Pause size={14} /> Pause</> : <><Play size={14} /> Activate</>}
                                </GlassButton>
                                <GlassButton variant="danger" onClick={() => handleDeleteFlow(flow.flow_id)}>
                                    <Trash2 size={14} /> Delete
                                </GlassButton>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    const renderFollowManager = () => {
        // Compute today's follow counts from analytics per platform (approximate)
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <GlassSelect
                        options={accountOptions}
                        value={selectedAccountId}
                        onChange={setSelectedAccountId}
                        placeholder="Select account..."
                    />
                    <GlassButton
                        variant="primary"
                        onClick={handlePopulateFollows}
                        disabled={populatingFollows || !selectedAccountId}
                    >
                        {populatingFollows ? (
                            <><RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> Getting...</>
                        ) : (
                            <><UserPlus size={16} /> Get Suggestions</>
                        )}
                    </GlassButton>
                </div>

                {/* Rate limit bars */}
                {analytics?.per_platform?.length > 0 && (
                    <GlassCard>
                        <p className="og-section-heading">Platform Follow Limits</p>
                        {analytics.per_platform.map((p: any) => {
                            const limit = PLATFORM_DAILY_LIMITS[p.platform] || 50;
                            const used = p.follows_executed || 0;
                            const pct = Math.min(100, Math.round((used / limit) * 100));
                            const cls = pct >= 90 ? 'danger' : pct >= 70 ? 'warning' : '';
                            return (
                                <div key={p.account_id} className="og-rate-bar-wrapper">
                                    <div className="og-rate-bar-label">
                                        <span>{p.platform} — {p.handle}</span>
                                        <span>{Math.min(used, limit)}/{limit} follows</span>
                                    </div>
                                    <div className="og-rate-bar-track">
                                        <div
                                            className={`og-rate-bar-fill ${cls}`}
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </GlassCard>
                )}

                {/* Suggested follows */}
                {followItems.length > 0 && (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <p className="og-section-heading">Suggested ({followItems.length})</p>
                            <GlassButton variant="primary" onClick={handleFollowAll}>
                                <Users size={16} /> Follow All
                            </GlassButton>
                        </div>
                        <div className="og-follow-list">
                            {followItems.map(item => (
                                <div key={item.follow_id} className="og-follow-item">
                                    <span className="og-follow-handle">{item.target_handle}</span>
                                    <GlassBadge variant="info">{item.platform}</GlassBadge>
                                    <span className="og-follow-reason">{item.reason}</span>
                                    <div className="og-follow-actions">
                                        <GlassButton variant="primary" onClick={() => handleExecuteFollow(item.follow_id)}>
                                            <UserPlus size={14} /> Follow
                                        </GlassButton>
                                        <GlassButton variant="secondary" onClick={() => handleSkipFollow(item.follow_id)}>
                                            Skip
                                        </GlassButton>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {followItems.length === 0 && !populatingFollows && (
                    <div className="og-empty">
                        <UserPlus size={32} style={{ opacity: 0.3, marginBottom: '8px' }} />
                        <p>No follow suggestions yet. Select an account and click "Get Suggestions".</p>
                    </div>
                )}

                {/* Follow history */}
                {analytics?.follow_history?.length > 0 && (
                    <GlassCard>
                        <p className="og-section-heading">Recent Follows</p>
                        <div className="og-follow-list">
                            {analytics.follow_history.slice(0, 10).map((f: any) => (
                                <div key={f.follow_id} className="og-follow-item">
                                    <span className="og-follow-handle">{f.target_handle}</span>
                                    <GlassBadge variant="info">{f.platform}</GlassBadge>
                                    <span className="og-follow-reason" style={{ marginLeft: 'auto' }}>
                                        {f.followed_at ? new Date(f.followed_at).toLocaleDateString() : ''}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </GlassCard>
                )}
            </div>
        );
    };

    const renderAnalytics = () => {
        if (!analytics) {
            return (
                <div className="og-empty">
                    <BarChart2 size={32} style={{ opacity: 0.3, marginBottom: '8px' }} />
                    <p>Loading analytics...</p>
                </div>
            );
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Per-platform cards */}
                {analytics.per_platform?.length > 0 && (
                    <GlassCard>
                        <p className="og-section-heading">Per-Platform Stats</p>
                        <div className="og-analytics-grid">
                            {analytics.per_platform.map((p: any) => (
                                <div key={p.account_id} className="og-analytics-card">
                                    <span className="og-analytics-card-title">{p.platform} — {p.handle}</span>
                                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '4px' }}>
                                        <div>
                                            <div className="og-analytics-card-value">{p.drafts_posted}</div>
                                            <div className="og-analytics-card-sub">Drafts Posted</div>
                                        </div>
                                        <div>
                                            <div className="og-analytics-card-value">{p.follows_executed}</div>
                                            <div className="og-analytics-card-sub">Follows</div>
                                        </div>
                                        <div>
                                            <div className="og-analytics-card-value">{p.drafts_pending}</div>
                                            <div className="og-analytics-card-sub">Pending</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </GlassCard>
                )}

                {/* DM Flows performance */}
                <GlassCard>
                    <p className="og-section-heading">DM Flow Performance</p>
                    <div className="og-analytics-grid">
                        <div className="og-analytics-card">
                            <span className="og-analytics-card-title">Total Sent</span>
                            <div className="og-analytics-card-value">{analytics.dm_flows?.total_sent ?? 0}</div>
                        </div>
                        <div className="og-analytics-card">
                            <span className="og-analytics-card-title">Total Replies</span>
                            <div className="og-analytics-card-value">{analytics.dm_flows?.total_replies ?? 0}</div>
                        </div>
                        <div className="og-analytics-card">
                            <span className="og-analytics-card-title">Reply Rate</span>
                            <div className="og-analytics-card-value">{analytics.dm_flows?.reply_rate_pct ?? 0}%</div>
                        </div>
                    </div>
                </GlassCard>

                {/* Recent activity */}
                {analytics.top_performing_drafts?.length > 0 && (
                    <GlassCard>
                        <p className="og-section-heading">Recent Engagement Activity</p>
                        <div className="og-draft-list">
                            {analytics.top_performing_drafts.map((d: any) => (
                                <div key={d.draft_id} className="og-draft-item">
                                    <div className="og-draft-meta">
                                        <GlassBadge variant="info">{d.platform}</GlassBadge>
                                        <GlassBadge variant="success">{d.draft_type}</GlassBadge>
                                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                            {d.posted_at ? new Date(d.posted_at).toLocaleDateString() : ''}
                                        </span>
                                    </div>
                                    <p style={{ fontSize: '0.88rem', color: 'var(--text)', margin: 0, lineHeight: 1.4 }}>
                                        {d.draft_text}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </GlassCard>
                )}
            </div>
        );
    };

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------
    if (!selectedProjectId) {
        return (
            <div className="organic-growth-module">
                <ModuleHeader
                    title="Organic Growth"
                    breadcrumb={['Campaigns', 'Organic Growth']}
                />
                <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
                    <TrendingUp size={64} style={{ opacity: 0.4, marginBottom: '16px' }} />
                    <h3 style={{ color: 'var(--text)', marginBottom: '8px' }}>Select a project</h3>
                    <p>Choose a project from the sidebar to view organic growth data</p>
                </div>
            </div>
        );
    }

    return (
        <div className="organic-growth-module">
            <ModuleHeader
                title="Organic Growth"
                breadcrumb={['Campaigns', 'Organic Growth']}
            />

            {/* Feedback banners */}
            {error && (
                <div style={{
                    padding: '12px 16px',
                    background: 'rgba(239,68,68,0.1)',
                    border: '1px solid rgba(239,68,68,0.3)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text)',
                    fontSize: '0.88rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                }}>
                    <span>{error}</span>
                    <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                        <X size={16} />
                    </button>
                </div>
            )}
            {successMsg && (
                <div style={{
                    padding: '12px 16px',
                    background: 'rgba(16,185,129,0.1)',
                    border: '1px solid rgba(16,185,129,0.3)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text)',
                    fontSize: '0.88rem',
                }}>
                    <CheckCircle size={14} style={{ marginRight: '6px' }} />
                    {successMsg}
                </div>
            )}

            <GlassTabs
                tabs={tabs}
                activeTab={activeTab}
                onChange={setActiveTab}
            />

            <div style={{ paddingTop: '4px' }}>
                {activeTab === 'overview' && renderOverview()}
                {activeTab === 'queue' && renderEngagementQueue()}
                {activeTab === 'dmflows' && renderDMFlows()}
                {activeTab === 'follows' && renderFollowManager()}
                {activeTab === 'analytics' && renderAnalytics()}
            </div>
        </div>
    );
};

export default OrganicGrowthModule;
